// Deformation cages (docs/claude/lattice.md): a box of control points that
// bends whatever lies in it. Each cage is a clamped B-spline volume (cubic, or
// of degree n−1 along an axis with fewer than four points) whose control points
// rest on their Greville points, so an untouched cage maps every point to itself
// and a cage's outer layer IS its surface. Only the points that moved are
// stored (`Lattice.offsets`); a point's displacement is the B-spline blend of
// those offsets. A stack applies its cages in order, each to the result of the
// ones before. Pure, and knows nothing of benches: coordinates are a frame's
// (along, out, up) — sculptBench.ts gives them their meaning.

import type { Lattice } from '../shared/types';

/** A point in a cage's frame: along, out, up (mm). */
export type F3 = [number, number, number];

/** What a cage bends: the solid's own extent in the frame, per axis. */
export interface FrameExtent { along: [number, number]; out: [number, number]; up: [number, number] }

export const AXES = ['along', 'out', 'up'] as const;
export type Axis = (typeof AXES)[number];

/** The colour a cage is drawn in (plan, sections, 3D). */
export const CAGE_COLOR = '#8a5cf6';

/** The fewest and most control points along one axis. */
export const LATTICE_MIN_POINTS = 2;
export const LATTICE_MAX_POINTS = 12;
/** A cage's outer layers kept at rest on each side where the solid runs on
 *  beyond it, so the bend leaves no crease at its edge. */
export const REST_LAYERS = 2;
/** Within this a box's side counts as the solid's own (mm). */
const SIDE_EPS = 0.5;

const counts = (c: Pick<Lattice, 'nx' | 'ny' | 'nz'>): [number, number, number] => [c.nx, c.ny, c.nz];

/** A cage's point count along an axis, clamped to what it may hold. */
export function clampCount(n: number): number {
  return Math.max(LATTICE_MIN_POINTS, Math.min(LATTICE_MAX_POINTS, Math.round(Number.isFinite(n) ? n : LATTICE_MIN_POINTS)));
}

/** A control point's index in `offsets`. */
export function pointIndex(c: Pick<Lattice, 'nx' | 'ny'>, i: number, j: number, k: number): number {
  return i + c.nx * (j + c.ny * k);
}

/** The control point an index names. */
export function pointIjk(c: Pick<Lattice, 'nx' | 'ny'>, index: number): [number, number, number] {
  const i = index % c.nx, rest = (index - i) / c.nx, j = rest % c.ny;
  return [i, j, (rest - j) / c.ny];
}

interface Basis { p: number; knots: number[]; greville: number[] }
const BASES = new Map<number, Basis>();

/** The clamped uniform knot vector of `n` control points and its Greville points. */
function basisOf(n: number): Basis {
  let b = BASES.get(n);
  if (b) return b;
  const p = Math.min(3, n - 1);
  const spans = n - p;
  const knots: number[] = [];
  for (let i = 0; i <= p; i++) knots.push(0);
  for (let i = 1; i < spans; i++) knots.push(i / spans);
  for (let i = 0; i <= p; i++) knots.push(1);
  const greville: number[] = [];
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let k = 1; k <= p; k++) s += knots[i + k];
    greville.push(s / p);
  }
  b = { p, knots, greville };
  BASES.set(n, b);
  return b;
}

/** Every basis function of `n` points at `t` ∈ [0, 1] (Cox–de Boor). */
export function basisAt(n: number, t: number): number[] {
  const { p, knots } = basisOf(n);
  const x = Math.max(0, Math.min(1, t));
  const m = knots.length - 1;
  // Degree 0: the span holding x (the last one closed at 1).
  let N = new Array<number>(m).fill(0);
  for (let i = 0; i < m; i++) {
    if (knots[i] < knots[i + 1] && ((x >= knots[i] && x < knots[i + 1]) || (x === 1 && knots[i + 1] === 1))) { N[i] = 1; break; }
  }
  for (let d = 1; d <= p; d++) {
    const next = new Array<number>(m - d).fill(0);
    for (let i = 0; i < m - d; i++) {
      const l = knots[i + d] - knots[i], r = knots[i + d + 1] - knots[i + 1];
      next[i] = (l > 0 ? ((x - knots[i]) / l) * N[i] : 0) + (r > 0 ? ((knots[i + d + 1] - x) / r) * N[i + 1] : 0);
    }
    N = next;
  }
  return N.slice(0, n);
}

/** Where control point (i, j, k) rests: its Greville point in the box. */
export function restPoint(c: Lattice, i: number, j: number, k: number): F3 {
  const g = (n: number, idx: number, [lo, hi]: [number, number]): number => lo + basisOf(n).greville[idx] * (hi - lo);
  return [g(c.nx, i, c.box.along), g(c.ny, j, c.box.out), g(c.nz, k, c.box.up)];
}

/** A control point's stored offset, or none. */
export function offsetOf(c: Lattice, i: number, j: number, k: number): F3 {
  const o = c.offsets?.[String(pointIndex(c, i, j, k))];
  return o ? [o[0] || 0, o[1] || 0, o[2] || 0] : [0, 0, 0];
}

/** Where control point (i, j, k) stands: its rest plus its offset. */
export function cagePoint(c: Lattice, i: number, j: number, k: number): F3 {
  const r = restPoint(c, i, j, k), o = offsetOf(c, i, j, k);
  return [r[0] + o[0], r[1] + o[1], r[2] + o[2]];
}

/** Whether a point lies in a cage's box (its sides included). */
export function inBox(c: Lattice, p: F3): boolean {
  const b = c.box;
  return p[0] >= b.along[0] && p[0] <= b.along[1] && p[1] >= b.out[0] && p[1] <= b.out[1] && p[2] >= b.up[0] && p[2] <= b.up[1];
}

const span = (r: [number, number]): number => r[1] - r[0];

/** How far a cage moves a point: the blend of its offsets — none outside its box. */
export function displacement(c: Lattice, p: F3): F3 {
  if (!c.offsets || !inBox(c, p)) return [0, 0, 0];
  const entries = Object.entries(c.offsets);
  if (!entries.length) return [0, 0, 0];
  const b = c.box;
  const bu = basisAt(c.nx, span(b.along) > 0 ? (p[0] - b.along[0]) / span(b.along) : 0);
  const bv = basisAt(c.ny, span(b.out) > 0 ? (p[1] - b.out[0]) / span(b.out) : 0);
  const bw = basisAt(c.nz, span(b.up) > 0 ? (p[2] - b.up[0]) / span(b.up) : 0);
  const out: F3 = [0, 0, 0];
  for (const [key, o] of entries) {
    const [i, j, k] = pointIjk(c, Number(key));
    const w = (bu[i] ?? 0) * (bv[j] ?? 0) * (bw[k] ?? 0);
    if (!w) continue;
    out[0] += w * (o[0] || 0); out[1] += w * (o[1] || 0); out[2] += w * (o[2] || 0);
  }
  return out;
}

/** A point bent by one cage. */
export function deform(c: Lattice, p: F3): F3 {
  const d = displacement(c, p);
  return [p[0] + d[0], p[1] + d[1], p[2] + d[2]];
}

/** The cages a stack applies: those not switched off, in order. */
export function activeCages(cages: readonly Lattice[] | undefined): Lattice[] {
  return (cages ?? []).filter((c) => c.enabled !== false && c.offsets && Object.keys(c.offsets).length > 0);
}

/** A point bent by a stack of cages in order, each by the result of the ones before. */
export function deformStack(cages: readonly Lattice[] | undefined, p: F3): F3 {
  let q = p;
  for (const c of activeCages(cages)) q = deform(c, q);
  return q;
}

/** Which of a cage's sides the solid runs on beyond (low, high per axis): its
 *  outer layers there stay at rest. */
export function openSides(c: Lattice, extent: FrameExtent): Record<Axis, [boolean, boolean]> {
  const side = (box: [number, number], ext: [number, number]): [boolean, boolean] => [box[0] > ext[0] + SIDE_EPS, box[1] < ext[1] - SIDE_EPS];
  return { along: side(c.box.along, extent.along), out: side(c.box.out, extent.out), up: side(c.box.up, extent.up) };
}

/** How a control point may move: per axis whether it may, or null when it stays
 *  at rest (an outer layer on an open side). The back layer — on the faces the
 *  solid stands against (`out` at the extent's low side) — never leaves them:
 *  it moves along and up only. */
export function pointFreedom(c: Lattice, extent: FrameExtent, i: number, j: number, k: number): Record<Axis, boolean> | null {
  const open = openSides(c, extent);
  const n = counts(c);
  const idx = [i, j, k];
  for (let a = 0; a < 3; a++) {
    const [lo, hi] = open[AXES[a]];
    if ((lo && idx[a] < REST_LAYERS) || (hi && idx[a] >= n[a] - REST_LAYERS)) return null;
  }
  const back = j === 0 && c.box.out[0] <= extent.out[0] + SIDE_EPS;
  return { along: true, out: !back, up: true };
}

/** Write a control point's offset, held to what it may do; a zero one is dropped. */
export function setOffset(c: Lattice, extent: FrameExtent, i: number, j: number, k: number, d: F3): void {
  const free = pointFreedom(c, extent, i, j, k);
  const key = String(pointIndex(c, i, j, k));
  const r = (v: number): number => Math.round(v * 10) / 10;
  const v: F3 = free ? [free.along ? r(d[0]) : 0, free.out ? r(d[1]) : 0, free.up ? r(d[2]) : 0] : [0, 0, 0];
  if (v[0] === 0 && v[1] === 0 && v[2] === 0) {
    if (c.offsets) { delete c.offsets[key]; if (!Object.keys(c.offsets).length) delete c.offsets; }
    return;
  }
  (c.offsets ??= {})[key] = v;
}

/** Which points a drag of one moves with it: the point alone, its row along the
 *  bench (every i), its column up (every k) or its cross-section (every j, k). */
export type CagePick = 'point' | 'row' | 'column' | 'across';
export const CAGE_PICKS: readonly CagePick[] = ['point', 'row', 'column', 'across'];

/** The points a drag of point (i, j, k) moves under `pick`, itself first. */
export function cageGroup(c: Pick<Lattice, 'nx' | 'ny' | 'nz'>, pick: CagePick, i: number, j: number, k: number): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [[i, j, k]];
  const add = (p: [number, number, number]): void => { if (p[0] !== i || p[1] !== j || p[2] !== k) out.push(p); };
  if (pick === 'row') for (let a = 0; a < c.nx; a++) add([a, j, k]);
  else if (pick === 'column') for (let a = 0; a < c.nz; a++) add([i, j, a]);
  else if (pick === 'across') for (let b = 0; b < c.ny; b++) for (let a = 0; a < c.nz; a++) add([i, b, a]);
  return out;
}

/** Move a group of points by `d` in the frame from where each stood (`from`, its
 *  offset then): an axis given null keeps each point's offset there as it is now.
 *  Each is held to what it may do (`setOffset`). */
export function shiftCagePoints(c: Lattice, extent: FrameExtent, pts: ReadonlyArray<readonly [number, number, number]>, from: readonly F3[], d: readonly [number | null, number | null, number | null]): void {
  pts.forEach(([i, j, k], n) => {
    const now = offsetOf(c, i, j, k), was = from[n] ?? now;
    setOffset(c, extent, i, j, k, [0, 1, 2].map((a) => (d[a] == null ? now[a] : was[a] + d[a]!)) as F3);
  });
}

/** Drop the offsets a cage no longer allows (after its box or counts changed):
 *  out-of-range indices and points that must stay at rest. */
export function tidyOffsets(c: Lattice, extent: FrameExtent): void {
  if (!c.offsets) return;
  const total = c.nx * c.ny * c.nz;
  for (const key of Object.keys(c.offsets)) {
    const idx = Number(key);
    if (!(idx >= 0 && idx < total)) { delete c.offsets[key]; continue; }
    const [i, j, k] = pointIjk(c, idx);
    setOffset(c, extent, i, j, k, c.offsets[key]);
  }
  if (c.offsets && !Object.keys(c.offsets).length) delete c.offsets;
}

/** A new cage around the whole solid: 5 along, 3 out, 3 up. */
export function defaultLattice(id: string, extent: FrameExtent, name?: string): Lattice {
  return { id, ...(name ? { name } : {}), box: { along: [...extent.along], out: [...extent.out], up: [...extent.up] }, nx: 5, ny: 3, nz: 3 };
}

/** The determinant of the 3 × 3 matrix of columns a, b, c. */
const det3 = (a: F3, b: F3, c: F3): number =>
  a[0] * (b[1] * c[2] - b[2] * c[1]) - b[0] * (a[1] * c[2] - a[2] * c[1]) + c[0] * (a[1] * b[2] - a[2] * b[1]);

/** Whether a cage turns space inside out somewhere in its box: where the bend's
 *  Jacobian determinant is no longer positive (sampled through the box). */
export function cageFolds(c: Lattice, samples = 6): boolean {
  if (!c.offsets || !Object.keys(c.offsets).length) return false;
  const b = c.box;
  const h = [span(b.along), span(b.out), span(b.up)].map((s) => Math.max(1e-3, s / (samples * 8)));
  for (let a = 0; a <= samples; a++) for (let o = 0; o <= samples; o++) for (let u = 0; u <= samples; u++) {
    const p: F3 = [b.along[0] + (span(b.along) * a) / samples, b.out[0] + (span(b.out) * o) / samples, b.up[0] + (span(b.up) * u) / samples];
    const col = (ax: number): F3 => {
      const lo = [...p] as F3, hi = [...p] as F3;
      const range = ax === 0 ? b.along : ax === 1 ? b.out : b.up;
      lo[ax] = Math.max(range[0], p[ax] - h[ax]); hi[ax] = Math.min(range[1], p[ax] + h[ax]);
      const dl = deform(c, lo), dh = deform(c, hi), w = hi[ax] - lo[ax] || 1;
      return [(dh[0] - dl[0]) / w, (dh[1] - dl[1]) / w, (dh[2] - dl[2]) / w];
    };
    if (det3(col(0), col(1), col(2)) <= 1e-6) return true;
  }
  return false;
}

/** The cage's lines through its control points (for drawing it): every row
 *  along each axis, as point lists in the frame. */
export function cageLines(c: Lattice): F3[][] {
  const out: F3[][] = [];
  for (let j = 0; j < c.ny; j++) for (let k = 0; k < c.nz; k++) out.push(Array.from({ length: c.nx }, (_, i) => cagePoint(c, i, j, k)));
  for (let i = 0; i < c.nx; i++) for (let k = 0; k < c.nz; k++) out.push(Array.from({ length: c.ny }, (_, j) => cagePoint(c, i, j, k)));
  for (let i = 0; i < c.nx; i++) for (let j = 0; j < c.ny; j++) out.push(Array.from({ length: c.nz }, (_, k) => cagePoint(c, i, j, k)));
  return out;
}

/** A cage's box: its twelve edges, each a line through its points as they stand
 *  (bent with them), the way another cage than the one being edited is shown. */
export function cageEdges(c: Lattice): F3[][] {
  const out: F3[][] = [];
  const I = [0, c.nx - 1], J = [0, c.ny - 1], K = [0, c.nz - 1];
  for (const j of J) for (const k of K) out.push(Array.from({ length: c.nx }, (_, i) => cagePoint(c, i, j, k)));
  for (const i of I) for (const k of K) out.push(Array.from({ length: c.ny }, (_, j) => cagePoint(c, i, j, k)));
  for (const i of I) for (const j of J) out.push(Array.from({ length: c.nz }, (_, k) => cagePoint(c, i, j, k)));
  return out;
}

/** `x` with `M·x = v` (Gaussian elimination with partial pivoting; `M` square). */
function solve(M: number[][], v: number[]): number[] {
  const n = v.length, A = M.map((r, i) => [...r, v[i]]);
  for (let c = 0; c < n; c++) {
    let piv = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(A[r][c]) > Math.abs(A[piv][c])) piv = r;
    [A[c], A[piv]] = [A[piv], A[c]];
    const d = A[c][c] || 1e-12;
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = A[r][c] / d;
      if (f) for (let k = c; k <= n; k++) A[r][k] -= f * A[c][k];
    }
  }
  return A.map((r, i) => r[n] / (r[i] || 1e-12));
}

/** The matrix taking `n` control values to the curve's values at its Greville points. */
const collocation = (n: number): number[][] => basisOf(n).greville.map((g) => basisAt(n, g));

/**
 * A cage refitted to new counts or a new box, keeping the bend it gives: the
 * new control offsets are solved (axis by axis, the blend being a tensor
 * product) so the new cage moves each of its own rest points as the old one
 * did — outside the old box, by nothing. Then held to what each point may do.
 */
export function refitLattice(c: Lattice, extent: FrameExtent, next: { nx?: number; ny?: number; nz?: number; box?: Lattice['box'] }): Lattice {
  const out: Lattice = {
    ...c,
    nx: clampCount(next.nx ?? c.nx), ny: clampCount(next.ny ?? c.ny), nz: clampCount(next.nz ?? c.nz),
    box: next.box ? { along: [...next.box.along], out: [...next.box.out], up: [...next.box.up] } : { along: [...c.box.along], out: [...c.box.out], up: [...c.box.up] },
  };
  delete out.offsets;
  if (!c.offsets || !Object.keys(c.offsets).length) return out;
  const n = [out.nx, out.ny, out.nz];
  const at = (i: number, j: number, k: number): number => i + n[0] * (j + n[1] * k);
  // The displacement wanted at each new rest point, one value list per component.
  const val: F3[] = [];
  for (let k = 0; k < n[2]; k++) for (let j = 0; j < n[1]; j++) for (let i = 0; i < n[0]; i++) val[at(i, j, k)] = displacement(c, restPoint(out, i, j, k));
  // Undo the blend along each axis in turn: on every line of points along it, solve its collocation.
  for (let a = 0; a < 3; a++) {
    const M = collocation(n[a]);
    const [b1, b2] = [0, 1, 2].filter((x) => x !== a);
    for (let u = 0; u < n[b1]; u++) for (let w = 0; w < n[b2]; w++) {
      const idx = (t: number): number => { const ijk = [0, 0, 0]; ijk[a] = t; ijk[b1] = u; ijk[b2] = w; return at(ijk[0], ijk[1], ijk[2]); };
      for (let comp = 0; comp < 3; comp++) {
        const x = solve(M, Array.from({ length: n[a] }, (_, t) => val[idx(t)][comp]));
        x.forEach((v, t) => { val[idx(t)][comp] = v; });
      }
    }
  }
  for (let k = 0; k < n[2]; k++) for (let j = 0; j < n[1]; j++) for (let i = 0; i < n[0]; i++) setOffset(out, extent, i, j, k, val[at(i, j, k)]);
  return out;
}

/** A copy of a cage under a new id (its offsets its own). */
export function copyLattice(c: Lattice, id: string, name?: string): Lattice {
  const out: Lattice = { ...c, id, box: { along: [...c.box.along], out: [...c.box.out], up: [...c.box.up] } };
  if (name) out.name = name; else delete out.name;
  if (c.offsets) out.offsets = Object.fromEntries(Object.entries(c.offsets).map(([k, v]) => [k, [...v] as F3]));
  return out;
}
